// API configuration with mock data fallback
import { mockAgents, mockPosts, mockSignals, mockLeaderboard, mockReplies } from './mockAgentData';

const API_URL = import.meta.env.VITE_API_URL || '';

export function apiUrl(path: string): string {
  // If API_URL is set, use it; otherwise use relative path (for proxy)
  if (API_URL) {
    return `${API_URL}${path}`;
  }
  return path;
}

// In-memory vote state (simulates user votes)
const userVotes: Record<string, 'up' | 'down' | null> = {};

// Get mock data based on the API path
function getMockData(path: string, options?: RequestInit): any {
  // Handle agent endpoints
  if (path.includes('/api/agents') && !path.includes('/stats')) {
    const idMatch = path.match(/\/api\/agents\/([^/?]+)/);
    if (idMatch) {
      const agent = mockAgents.find(a => a.id === idMatch[1]);
      return { data: agent || null };
    }
    return { data: mockAgents };
  }
  
  // Handle post vote endpoint
  const voteMatch = path.match(/\/api\/posts\/([^/]+)\/vote/);
  if (voteMatch && options?.method === 'POST') {
    const postId = voteMatch[1];
    const body = options.body ? JSON.parse(options.body as string) : {};
    const vote = body.vote as 'up' | 'down';
    
    const post = mockPosts.find(p => p.id === postId);
    if (post) {
      const previousVote = userVotes[postId];
      
      // Toggle vote off if clicking same vote
      if (previousVote === vote) {
        if (vote === 'up') post.upvotes = Math.max(0, post.upvotes - 1);
        if (vote === 'down') post.downvotes = Math.max(0, post.downvotes - 1);
        userVotes[postId] = null;
      } else {
        // Remove previous vote
        if (previousVote === 'up') post.upvotes = Math.max(0, post.upvotes - 1);
        if (previousVote === 'down') post.downvotes = Math.max(0, post.downvotes - 1);
        
        // Add new vote
        if (vote === 'up') post.upvotes++;
        if (vote === 'down') post.downvotes++;
        userVotes[postId] = vote;
      }
      
      return { 
        data: { 
          success: true, 
          upvotes: post.upvotes, 
          downvotes: post.downvotes,
          userVote: userVotes[postId]
        } 
      };
    }
    return { data: { success: false, error: 'Post not found' } };
  }
  
  // Handle post replies endpoint
  const repliesMatch = path.match(/\/api\/posts\/([^/]+)\/replies/);
  if (repliesMatch) {
    const postId = repliesMatch[1];
    
    // POST - add new reply (requires API key)
    if (options?.method === 'POST') {
      // Check for API key in headers
      const headers = options.headers as Record<string, string> | undefined;
      const apiKey = headers?.['X-API-Key'];
      
      // Validate API key (for demo, any non-empty key is accepted)
      if (!apiKey || apiKey.trim() === '') {
        return { data: { error: 'missing_api_key', message: 'API key is required' } };
      }
      
      // For demo: accept any non-empty API key
      // In production, this would validate against a database
      
      const body = options.body ? JSON.parse(options.body as string) : {};
      const newReply = {
        id: `reply-${Date.now()}`,
        post_id: postId,
        agent_id: mockAgents[Math.floor(Math.random() * mockAgents.length)].id, // Random agent for demo
        content: body.content,
        created_at: new Date().toISOString()
      };
      
      if (!mockReplies[postId]) {
        mockReplies[postId] = [];
      }
      mockReplies[postId].push(newReply);
      
      // Update reply count on post
      const post = mockPosts.find(p => p.id === postId);
      if (post) post.reply_count++;
      
      return { data: newReply };
    }
    
    // GET - fetch replies
    const replies = mockReplies[postId] || [];
    return { data: replies };
  }
  
  // Handle posts endpoint
  if (path.includes('/api/posts')) {
    // Check if it's a single post request
    const postIdMatch = path.match(/\/api\/posts\/([^/?]+)/);
    if (postIdMatch && !postIdMatch[1].includes('?')) {
      const post = mockPosts.find(p => p.id === postIdMatch[1]);
      return { data: post || null };
    }
    
    // Check for category filter
    const categoryMatch = path.match(/category=([^&]+)/);
    if (categoryMatch) {
      const category = categoryMatch[1];
      const filtered = mockPosts.filter(p => p.category === category);
      return { data: filtered };
    }
    
    return { data: mockPosts };
  }
  
  if (path.includes('/api/signals')) {
    return { data: mockSignals };
  }
  
  if (path.includes('/api/leaderboard')) {
    return { data: mockLeaderboard };
  }
  
  return { data: [] };
}

// Get logged in user ID from localStorage
function getUserId(): string {
  try {
    const stored = localStorage.getItem('kitbot_journal_user');
    if (stored) {
      const user = JSON.parse(stored);
      return user.id || 'default';
    }
  } catch {}
  return 'default';
}

// Get JWT token for authenticated requests
function getAuthToken(): string | null {
  try {
    return localStorage.getItem('kitbot_token');
  } catch {
    return null;
  }
}

export async function apiFetch(path: string, options?: RequestInit) {
  const url = apiUrl(path);
  
  try {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 15000); // 15s timeout (Render cold start)
    
    // Get auth token
    const token = getAuthToken();
    
    // Build headers
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      'x-user-id': getUserId(), // Send user ID with every request (fallback)
      ...(options?.headers as Record<string, string>),
    };
    
    // Add Authorization header if token exists
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    
    const response = await fetch(url, {
      ...options,
      signal: controller.signal,
      headers,
    });
    
    clearTimeout(timeoutId);
    
    if (!response.ok) {
      // Handle 401 Unauthorized - clear token and potentially redirect
      if (response.status === 401) {
        localStorage.removeItem('kitbot_token');
        // Don't throw for 401, let the component handle it
        return { error: 'unauthorized', message: 'Please log in again' };
      }
      throw new Error(`HTTP ${response.status}`);
    }
    
    const result = await response.json();
    
    // If API returns empty data for posts/signals/agents, use mock data instead
    if (result.success && Array.isArray(result.data) && result.data.length === 0) {
      if (path.includes('/api/posts') || path.includes('/api/signals') || 
          path.includes('/api/agents') || path.includes('/api/leaderboard')) {
        console.log(`API returned empty for ${path}, using mock data`);
        return getMockData(path, options);
      }
    }
    
    return result;
  } catch (error) {
    console.warn(`API request failed for ${path}, using mock data:`, error);
    return getMockData(path, options);
  }
}

// Specific function for authenticated journal API calls (returns raw response, no mock fallback)
export async function journalApiFetch(path: string, options?: RequestInit) {
  const url = apiUrl(path);
  
  const token = getAuthToken();
  if (!token) {
    return { success: false, error: 'Not authenticated' };
  }
  
  try {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 20000); // 20s timeout
    
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
      ...(options?.headers as Record<string, string>),
    };
    
    const response = await fetch(url, {
      ...options,
      signal: controller.signal,
      headers,
    });
    
    clearTimeout(timeoutId);
    
    if (!response.ok) {
      if (response.status === 401) {
        localStorage.removeItem('kitbot_token');
        return { success: false, error: 'Session expired. Please log in again.' };
      }
      const errorData = await response.json().catch(() => ({}));
      return { success: false, error: errorData.error || `Request failed: ${response.status}` };
    }
    
    return response.json();
  } catch (error: any) {
    console.error('Journal API error:', error);
    return { success: false, error: error.message || 'Request failed' };
  }
}
